import type { NavChild, NavItem } from './navigation'

/** '/inbox?view=unread' -> { path: '/inbox', params: { view: 'unread' } } */
export function splitDestination(to: string): {
  path: string
  params: URLSearchParams
} {
  const q = to.indexOf('?')
  if (q === -1) return { path: to, params: new URLSearchParams() }
  return { path: to.slice(0, q), params: new URLSearchParams(to.slice(q + 1)) }
}

/**
 * Does the current route sit inside this item?
 *
 * A prefix match, so /bots/:botId keeps "Chatbots" lit and
 * /team/departments keeps "Team" lit. The dashboard (path '') is exact —
 * otherwise it would be highlighted on every screen in the workspace.
 */
export function isItemActive(item: NavItem, base: string, pathname: string): boolean {
  const target = `${base}${item.path}`
  if (item.path === '') return pathname === base || pathname === `${base}/`
  return pathname === target || pathname.startsWith(`${target}/`)
}

/**
 * Is this exact sub-destination the one on screen?
 *
 * The comparison covers every search key any SIBLING uses, not just this
 * child's own. Without that, '/inbox' (the "All" child) would stay lit while
 * '/inbox?view=unread' is open, because "no view param" is a subset of every
 * url. Keys no sibling claims — ?new=1 from a quick action, a future ?q= —
 * are ignored, so opening the create dialog does not blank the selection.
 */
export function isChildActive(
  child: NavChild,
  siblings: readonly NavChild[],
  base: string,
  pathname: string,
  search: URLSearchParams,
): boolean {
  const { path, params } = splitDestination(child.to)
  if (pathname !== `${base}${path}`) return false

  const claimed = new Set<string>()
  for (const sibling of siblings) {
    for (const key of splitDestination(sibling.to).params.keys()) claimed.add(key)
  }

  for (const key of claimed) {
    if ((search.get(key) ?? null) !== (params.get(key) ?? null)) return false
  }
  return true
}
